'use client';

import { useEffect, useState } from 'react';
import { getAdsConfig } from '@/lib/api-client';

/** Module-level cache so every ad renderer shares one fetch. Invalidated on
 *  hard refresh — stale-while-revalidate is fine here since admin changes
 *  don't need to propagate instantly. */
let cached: string[] | null = null;
let inflight: Promise<string[]> | null = null;

async function load(): Promise<string[]> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = getAdsConfig()
    .then((c) => { cached = c.globalExcludedPages ?? []; return cached; })
    .catch(() => { cached = []; return cached; })
    .finally(() => { inflight = null; });
  return inflight;
}

/** Returns the admin-configured list of pages where all ads should be
 *  suppressed. Empty array while loading (fail-open: ads show). */
export function useGlobalAdExclusions(): string[] {
  const [list, setList] = useState<string[]>(cached ?? []);
  useEffect(() => {
    if (cached) return;
    void load().then(setList);
  }, []);
  return list;
}

/** True when `pathname` is covered by either the slot's per-slot exclusions
 *  or the global exclusions. Supports the `/stocks/:ticker` wildcard the
 *  existing renderers already understand. */
export function isAdExcluded(excludedPages: string[], globalExcluded: string[], pathname: string): boolean {
  const all = [...excludedPages, ...globalExcluded];
  return all.some((ep) => {
    if (ep === '/stocks/:ticker') {
      const seg = pathname.split('/')[2] ?? '';
      return /^\/stocks\/[^/]+$/.test(pathname) &&
        !['screener', 'earnings', 'lists', 'compare'].includes(seg);
    }
    if (ep === '/dashboard-tile') return false; // virtual page — handled by ad-tile widget itself
    return pathname === ep || pathname.startsWith(ep + '/');
  });
}
